import { Cast, Play, Sparkles, Users, X } from "lucide-react";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import type { Game } from "../../data/games";
import { GameArtwork } from "../GameArtwork/GameArtwork";
import styles from "./GameDetailDialog.module.css";

interface GameDetailDialogProps {
  game: Game;
  simulationAvailable: boolean;
  onClose: () => void;
  onStartGame: (game: Game) => void;
  onChooseDemo: (gameId: string) => void;
}

const focusableSelector = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function GameDetailDialog({
  game,
  simulationAvailable,
  onClose,
  onStartGame,
  onChooseDemo,
}: GameDetailDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ??
          [],
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements.at(-1);

      if (!firstElement || !lastElement) {
        event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.backdrop} onMouseDown={handleBackdropClick}>
      <div
        className={styles.dialog}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="game-dialog-title"
        aria-describedby="game-dialog-description"
      >
        <button
          className={styles.closeButton}
          ref={closeButtonRef}
          type="button"
          aria-label="Stäng speldetaljer"
          onClick={onClose}
        >
          <X aria-hidden="true" />
        </button>

        <div className={styles.artwork}>
          <GameArtwork
            imageUrl={game.imageUrl}
            title={game.title}
            fallbackLabel={game.fallbackLabel}
            categoryId={game.categoryId}
            priority
          />
        </div>

        <div className={styles.content}>
          <span className={styles.category}>{game.category}</span>
          <h2 id="game-dialog-title">{game.title}</h2>
          <p id="game-dialog-description">{game.description}</p>
          <p className={styles.players}>
            <Users aria-hidden="true" />
            {game.minPlayers}–{game.maxPlayers} spelare
          </p>

          {simulationAvailable ? (
            <div className={styles.actions}>
              <button
                className={styles.primaryAction}
                type="button"
                onClick={() => onStartGame(game)}
              >
                <Play aria-hidden="true" fill="currentColor" />
                Starta spel
              </button>
              <button
                className={styles.secondaryAction}
                type="button"
                onClick={() =>
                  setMessage(
                    "Anslutning till Android TV kommer i nästa steg.",
                  )
                }
              >
                <Cast aria-hidden="true" />
                Visa på TV
              </button>
            </div>
          ) : (
            <>
              <div className={styles.demoNotice}>
                <Sparkles aria-hidden="true" />
                <p>
                  Den kompletta simuleringen finns just nu för Quizkväll och
                  Vem i rummet?
                </p>
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.primaryAction}
                  type="button"
                  onClick={() => onChooseDemo("quiz-night")}
                >
                  Testa Quizkväll
                </button>
                <button
                  className={styles.secondaryAction}
                  type="button"
                  onClick={() => onChooseDemo("who-in-the-room")}
                >
                  Testa Vem i rummet
                </button>
              </div>
            </>
          )}

          <p className={styles.liveMessage} aria-live="polite" aria-atomic="true">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}
